import { PaymentInvoice, fillTemplate, formatYMD, overdueToPaymentInvoices } from '../core/utils'
import React, { useEffect, useRef, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { SECTION_TYPES } from 'containers/Warehouse'
import ArrowBtn from 'components/buttons/ArrowBtn'
import useTranslation from 'hooks/useTranslation'
import BackBtn from 'components/buttons/BackBtn'
import { contractDocument } from 'rest/urls'
import Loading from 'components/Loading'
import Payments from '../core/Payments'
import { GET } from 'rest/request'

export type PayNowVariant = 'termination' | 'extension'

const LABELS: Record<PayNowVariant, { saved: string, savedDate: string, failed: string, summary: string }> = {
  termination: { saved: 'rentalFinishSaved', savedDate: 'rentalFinishSavedDate', failed: 'invoiceFailed', summary: '' },
  extension: { saved: 'extensionSaved', savedDate: 'extensionSavedDate', failed: 'invoiceFailedExtension', summary: 'extensionSummary' },
}

interface PayNowProps {
  contractId: string
  /** proces, z ktorego pochodzi dokument (domyslnie zakonczenie) */
  variant?: PayNowVariant
  /** termination.payNow z odpowiedzi zapisu rezygnacji / payNow z odpowiedzi przedluzenia */
  payNow?: payments.TerminationPayNow | null
  /** nieoplacone faktury umowy z wyceny sprzed zapisu - placone razem z nowym dokumentem */
  overdueInvoices: payments.QuoteOverdueInvoice[]
  selectedData: payments.SelectedData
  /** wyjscie z procesu (zakonczenie / przedluzenie jest juz zapisane) */
  close: (remotePaymentId?: string) => void
}

const POLL_INTERVAL = 2500
const PENDING_TIMEOUT = 60_000

const WAITING_STATUSES: payments.TerminationDocumentStatus[] = ['pending', 'awaiting_approval']

const PayNow: React.FC<PayNowProps> = ({ contractId, variant = 'termination', payNow, overdueInvoices, selectedData, close }) => {
  const { id } = useParams<warehouse.PageParams>()
  const { replace } = useHistory()
  const { t } = useTranslation('warehouse.terminationCalendar')
  const labels = LABELS[variant]

  const [documents, setDocuments] = useState<payments.TerminationDocument[]>(payNow?.documents ?? [])
  const [timedOut, setTimedOut] = useState(false)
  const startedAt = useRef(Date.now())

  const waitingDocuments = documents.filter(document => WAITING_STATUSES.includes(document.status))
  const isWaiting = waitingDocuments.length > 0 && !timedOut
  const isAwaitingApproval = waitingDocuments.some(document => document.status === 'awaiting_approval')

  useEffect(() => {
    if (!isWaiting) return
    let cancelled = false
    const timeout = setTimeout(async () => {
      // dokumenty "pending" maja limit czasu, "awaiting_approval" czekaja bez limitu
      const onlyPending = documents
        .filter(document => WAITING_STATUSES.includes(document.status))
        .every(document => document.status === 'pending')
      if (onlyPending && Date.now() - startedAt.current > PENDING_TIMEOUT) {
        setTimedOut(true)
        return
      }
      const updated = await Promise.all(documents.map(async document => {
        if (!WAITING_STATUSES.includes(document.status)) return document
        try {
          const response: payments.TerminationDocument = await GET(contractDocument(contractId, document.id))
          return response?.status ? response : document
        } catch (error) {
          console.error(error)
          return document
        }
      }))
      if (!cancelled) setDocuments(updated)
    }, POLL_INTERVAL)
    return () => {
      cancelled = true
      clearTimeout(timeout)
    }
  }, [isWaiting, documents, contractId])

  if (isWaiting) {
    return <div className='resignation__main'>
      <div className='title'>
        <div>{t(labels.saved)}</div>
        <div>{t(isAwaitingApproval ? 'awaitingApproval' : 'preparingInvoice')}</div>
      </div>
      <Loading middleOfPage />
      <div className='resignation__buttons'>
        <BackBtn
          text={t('payLater')}
          action={() => close()}
        />
      </div>
    </div>
  }

  const issuedDocuments = documents.filter(document => document.status === 'issued' && document.canPay && Number(document.remainingAmount) > 0)
  const invoices: PaymentInvoice[] = [
    ...issuedDocuments.map(({ id, remainingAmount, object }) => ({ invoiceId: id, amount: remainingAmount, object })),
    ...overdueToPaymentInvoices(overdueInvoices)
  ]

  const hasFailed = documents.some(document => document.status === 'failed') || (!!payNow?.errors?.length && documents.length === 0)
  const isDelayed = timedOut && waitingDocuments.length > 0
  const notice = hasFailed
    ? t(labels.failed)
    : isDelayed
      ? t('invoiceDelayed')
      : null

  if (invoices.length > 0) {
    return <Payments
      invoices={invoices}
      selectedData={selectedData}
      summaryTop={variant === 'termination' ? undefined : t(labels.summary)}
      close={close}
      additionalElement={notice && <div className='resignation__info --error'>{notice}</div>}
    />
  }

  return <div className='resignation__main'>
    <div className='title'>
      <div>{t(labels.saved)}</div>
      <div>{fillTemplate(t(labels.savedDate), { date: formatYMD(selectedData.date) })}</div>
    </div>
    {notice && <div className='resignation__info --error'>{notice}</div>}
    <div className='resignation__buttons'>
      {isDelayed && <ArrowBtn
        enlarged
        text={t('goToInvoices')}
        action={() => replace(`/warehouses/${id}/${SECTION_TYPES.invoices}`)}
      />}
      <BackBtn
        text={t('backToRentalDetails')}
        action={() => close()}
      />
    </div>
  </div>
}

export default PayNow
